import styled, { css } from 'styled-components';
import { token } from '@bynder/design-system';
import { VISUALS } from '../../../constants/styles';
import { TypographyHeading } from '../../../types/typography';
import { getTypography } from './constants';

export const TableCellEditor = styled.div`
  [contenteditable] {
    padding: ${token.spacing3};

    &:hover {
      background-color: transparent;
    }
  }
`;

interface TableProps {
  $showAsEditable?: boolean;
}

export const TableContainer = styled.div`
  display: flex;
`;

export const Table = styled.table<TableProps>`
  position: relative;
  width: 100%;
  table-layout: fixed;
  border-spacing: 0;
  ${props => getTypography(TypographyHeading.NORMAL, props.theme)};

  ${props =>
    props.$showAsEditable
      ? css`
          width: calc(100% + 15px);
          margin-left: -15px;
          margin-top: 8px;

          tr:first-child > td {
            border-bottom-color: ${token.gray200};
          }

          td:first-child {
            width: 16px;
            height: 100%;
          }
        `
      : css`
          margin-top: 0px;
        `}

  > ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;

    > li {
      display: inline-block;
      flex: 1 0 auto;
    }
  }

  & ::after,
  & ::before {
    box-sizing: content-box;
  }
`;

interface CellProps {
  $highlightedAddRow?: boolean;
  $highlightedAddCol?: boolean;
  $highlightedRow?: boolean;
  $highlightedCol?: boolean;
  $selectedRow?: boolean;
  $selectedCol?: boolean;
  $hoveredDeleteButton?: boolean;
  $showAsEditable?: boolean;
  $lastRow?: boolean;
  $lastColumn?: boolean;
}

const tableVerticalBorders = (color: string) => css`
  content: '';
  position: absolute;
  top: 0;
  width: 100%;
  height: calc(100% + 1px);
  left: -1px;
  border-right: 1px solid ${color};
  border-left: 1px solid ${color};
`;

const tableBottomBorder = (color: string) => css`
  content: '';
  position: absolute;
  top: -1px;
  width: 100%;
  height: calc(100% + 1px);
  border-bottom: 1px solid ${color};
`;

const tableRightBorder = (color: string) => css`
  content: '';
  position: absolute;
  top: -1px;
  width: 100%;
  height: 100%;
  border-right: 1px solid ${color};
`;

const tableHorizontalBorders = (color: string) => css`
  content: '';
  position: absolute;
  top: -1px;
  height: 100%;
  left: 0;
  width: calc(100% + 1px);
  border-bottom: 1px solid ${color};
  border-top: 1px solid ${color};
`;

const selectedDeleteBackground = css`
  background: ${token.red500a10};
`;

export const Cell = styled.td<CellProps>`
  position: relative;
  ${props =>
    props.$showAsEditable
      ? css`
          border-right: 1px solid ${token.gray200};
          border-bottom: 1px solid ${token.gray200};
        `
      : css`
          border: 1px solid ${token.gray200};
        `}

  &::after {
    ${props =>
      props.$highlightedAddRow &&
      css`
        content: '';
        position: absolute;
        top: 0;
        right: -1px;
        height: 100%;
        width: calc(100% + 1px);
        border-bottom: 1px solid ${token.gray800};
      `}

    ${props =>
      props.$highlightedAddCol &&
      css`
        content: '';
        position: absolute;
        top: 0;
        right: -1px;
        height: calc(100% + 1px);
        width: 1px;
        border-right: 1px solid ${token.gray800};
      `}
  }

  ${props => {
    if (props.$highlightedCol) {
      return css`
        &::after {
          ${tableVerticalBorders(token.gray200a)}
          ${props.$lastRow && tableBottomBorder(token.gray200a)}
        }
        background: ${token.gray50a};
      `;
    }
  }}

  ${props => {
    if (props.$highlightedRow) {
      return css`
        &::after {
          ${tableHorizontalBorders(token.gray200a)}
          ${props.$lastColumn && tableRightBorder(token.gray200a)}
        }
        background: ${token.gray50a};
      `;
    }
  }}


  ${props => {
    const selectedVerticalBorders = css<{
      hoveredDeleteButtonBorderColor: boolean;
      hoveredDeleteButton: boolean;
    }>`
      &::before {
        ${tableVerticalBorders(token.gray800)}
        ${props.$hoveredDeleteButton && hoveredDeleteButtonBorderColor}
      }
      &::after {
        ${props.$lastRow && tableBottomBorder(token.gray800)}
        ${props.$hoveredDeleteButton && hoveredDeleteButtonBorderColor}
      }
    `;

    if (props.$selectedCol) {
      return css`
        ${props.$hoveredDeleteButton && selectedDeleteBackground}

        ${selectedVerticalBorders}
      `;
    }
  }}

  ${props => {
    const selectedHorizontalBorders = css`
      &::before {
        ${tableHorizontalBorders(token.gray800)}
        ${props.$hoveredDeleteButton && hoveredDeleteButtonBorderColor}
      }
      &::after {
        ${props.$lastColumn && tableRightBorder(token.gray800)}
        ${props.$hoveredDeleteButton && hoveredDeleteButtonBorderColor}
      }
    `;

    if (props.$selectedRow) {
      return css`
        ${props.$hoveredDeleteButton && selectedDeleteBackground}
        ${selectedHorizontalBorders}
      `;
    }
  }}
`;

export enum DIRECTION {
  ROW = 'ROW',
  COLUMN = 'COLUMN',
}

interface ControlProps {
  direction?: DIRECTION;
}

export const TableControl = styled(Cell)<ControlProps>`
  padding: 0;
  border-top: none;
  border-left: none;
  border-color: transparent;
  text-align: right;
  vertical-align: bottom;
  border-bottom: 1px solid ${token.gray200};
  border-right: 1px solid ${token.gray200};
`;

export const AddButton = styled.button<ControlProps>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  position: absolute;
  border: none;
  padding: 0;
  background: none;
  color: white;
  text-align: center;
  outline: none;
  z-index: 1;

  ${props =>
    props.direction === DIRECTION.ROW
      ? css`
          transform: translate(-160%, calc(-50% + 1px));
        `
      : css`
          transform: translate(calc(-50% + 1px), -160%);
        `}

  &:before {
    content: '';
    position: absolute;
    left: calc(50% - 2px);
    top: calc(50% - 2px);
    width: 4px;
    height: 4px;
    background: ${token.gray200a};
    border-radius: ${token.radiusCircle};
  }

  &:hover {
    cursor: pointer;
    color: white;

    &:before {
      display: none;
    }

    > svg {
      display: initial;
    }
  }

  > svg {
    display: none;
    color: ${props => (props.disabled ? token.gray300a : token.gray800a)};
    border-radius: ${token.radiusCircle};
    fill: currentColor;
    stroke: currentColor;
  }
`;

interface ColumnControlProps {
  selectedCol?: boolean;
  hoveredDeleteButton?: boolean;
}

const GenericControl = styled.div`
  background: ${token.gray25a};

  &:hover {
    background: ${token.gray300a};
    cursor: pointer;
  }
`;

const hoveredDeleteButtonBorderColor = css<{ hoveredDeleteButton?: boolean }>`
  border-color: ${props =>
    props.hoveredDeleteButton ? token.red500 : token.gray800a} !important;
`;

const selectedButtonStyles = css<{ hoveredDeleteButton?: boolean }>`
  background: ${props =>
    props.hoveredDeleteButton ? token.red500 : token.gray800a};
  &::after {
    border-color: ${props =>
      props.hoveredDeleteButton ? token.red500 : token.gray800a} !important;
  }
`;

export const ColumnControl = styled(GenericControl)<ColumnControlProps>`
  height: 16px;

  &:hover::after {
    content: '';
    position: absolute;
    top: 0;
    right: -1px;
    height: calc(100% - 1px);
    width: 100%;
    border: 1px solid ${token.gray200a};
  }

  &,
  &:hover {
    ${props =>
      props.selectedCol &&
      css`
        ${selectedButtonStyles}
        &::after {
          content: '';
          position: absolute;
          top: 0;
          right: -1px;
          height: calc(100% - 1px);
          width: 100%;
          border: 1px solid ${token.gray800};
        }
      `}
  }
`;

interface RowControlProps {
  highlightedAddRow?: boolean;
  highlightedAddCol?: boolean;
  selectedRow?: boolean;
  hoveredDeleteButton?: boolean;
}

export const RowControl = styled(GenericControl)<RowControlProps>`
  height: 38px;

  &,
  &:hover {
    ${props =>
      props.selectedRow &&
      css`
        ${selectedButtonStyles}
        &::after {
          content: '';
          position: absolute;
          top: -1px;
          height: 100%;
          left: 0;
          width: calc(100% - 1px);
          border: 1px solid ${token.gray800};
        }
      `}
  }
`;

const ControlMenu = styled.div`
  display: flex;
  align-items: center;
  border-radius: ${token.radiusMedium};
  box-shadow:
    0 0 0 1px ${token.gray50a},
    0 2px 6px 0 ${token.gray50a};
  background-color: ${token.white};
  position: absolute;
  z-index: ${VISUALS.ZINDEX_FLOATING_MENU};
`;

export const ColumnControlMenu = styled(ControlMenu)`
  transform: translateY(-150%);
`;

export const RowControlMenu = styled(ControlMenu)`
  transform: translate(50%, calc(-100% - 8px));
`;
